import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RatingStar from '../RatingStar';
import CustomerReviewReply from './CustomerReviewReply';
import styles from './index.module.scss';
import CustomerReviewModal from '../CustomerReviewModal';
import VerifiedBuyerModal from './VerifiedBuyerModal';
import { deviceWidth } from '@/utils/deviceWidth';
import ReactTooltip from 'react-tooltip';
import { ProductReviewMedia } from './ProductReviewMedia';
import { getProductReviews } from 'src/redux/actions';
import { getThemeColor } from 'src/utils/getThemeColor';
import classNames from 'classnames';
import { ProductReviewModal } from './ProductReviewModal';

const REVIEW_TYPE = {
  ALL: 0,
  WITH_MEDIA: 1,
};

const ReviewsSection = ({ product, shouldReviewItem }) => {
  const themeColor = getThemeColor();
  const dispatch = useDispatch();

  const [openCustomerReviewModal, setOpenCustomerReviewModal] = useState(false);

  const [productReviewModalState, setProductReviewModalState] = useState({
    show: false,
    type: REVIEW_TYPE.WITH_MEDIA,
    activeReviewIndex: 0,
    reviewMediaIndex: 0,
  });

  const [openVerifiedModal, setOpenVerifiedModal] = useState(false);
  const { userData, storeInfo } = useSelector((state) => ({
    userData: state.userReducer,
    storeInfo: state.storeReducer.store,
  }));

  const [productReviewCount, setProductReviewCount] = useState({
    avgRating: 0,
    ratingCount: 0,
    reviewCount: 0,
  });

  const [allReviews, setAllReview] = useState([]);
  const [reviewsWithMedia, setReviewsWithMedia] = useState([]);

  const productReviewModalReviews =
    productReviewModalState.type === REVIEW_TYPE.ALL ? allReviews : reviewsWithMedia;

  const [allReviewPaginationConfig, setAllReviewPaginationConfig] = useState({
    pageNum: 1,
    isNextPage: true,
  });

  const [reviewWithMediaPaginationConfig, setReviewWithMediaPaginationConfig] = useState({
    pageNum: 1,
    isNextPage: true,
  });

  useEffect(() => {
    getAllProductReviews();
    getProductReviewsWithMedia();
  }, [product]);

  function getReviews(isReset, isAllReviews) {
    const reviewConfig = isAllReviews
      ? allReviewPaginationConfig
      : reviewWithMediaPaginationConfig;
    const setReviewConfig = isAllReviews
      ? setAllReviewPaginationConfig
      : setReviewWithMediaPaginationConfig;
    const reviews = isAllReviews ? allReviews : reviewsWithMedia;
    const setReviews = isAllReviews ? setAllReview : setReviewsWithMedia;

    const currentPage = !isReset ? reviewConfig.pageNum : 1;
    const isNextPage = !isReset ? reviewConfig.isNextPage : true;

    if (!isNextPage) {
      return;
    }

    const params = {
      storeId: storeInfo?.store_id,
      itemId: product?.id,
      pageNum: currentPage,
      media: isAllReviews ? REVIEW_TYPE.ALL : REVIEW_TYPE.WITH_MEDIA,
    };

    dispatch(
      getProductReviews(params, (status, data) => {
        if (status) {
          const isNextPage = currentPage < data?.total_page;
          const newList = !isReset
            ? [...reviews, ...(data?.review_rating || [])]
            : data?.review_rating || [];

          isAllReviews &&
            setProductReviewCount({
              avgRating: newList?.[0]?.avg_rating,
              ratingCount: data?.ratings_count,
              reviewCount: data?.reviews_count,
            });

          setReviewConfig({
            pageNum: currentPage + 1,
            isNextPage: isNextPage,
          });
          setReviews(newList);
        }
      })
    );
  }

  function getAllProductReviews(isReset = false) {
    getReviews(isReset, true);
  }

  function getProductReviewsWithMedia(isReset = false) {
    getReviews(isReset, false);
  }

  const loggedInUserReview = allReviews?.find((review) =>
    userData?.data?.phone
      ? review?.phone === userData?.data?.phone
      : review?.user_id === userData?.data?.user_id
  );

  const handleCustomerReviewModalOpen = () => {
    if (!shouldReviewItem() && deviceWidth > 767) return;
    if (!shouldReviewItem() && deviceWidth <= 767) {
      setOpenVerifiedModal(true);
      return;
    }
    setOpenCustomerReviewModal(true);
  };

  function handleProductReviewModalOpen(
    reviewIndex,
    mediaIndex,
    type = REVIEW_TYPE.WITH_MEDIA
  ) {
    setProductReviewModalState({
      show: true,
      type,
      activeReviewIndex: reviewIndex,
      reviewMediaIndex: mediaIndex,
    });
  }

  const handleCustomerReviewModalClose = () => {
    setOpenCustomerReviewModal(false);
    getAllProductReviews(true);
    getProductReviewsWithMedia(true);
  };

  return (
    <>
      <div id="reviews-section" className={styles.reviewsSectionContainer}>
        <div className="flex justify-between tw-flex-col tw-gap-[32px] md:tw-flex-row md:tw-items-end">
          <h2 className="mv0 fw6 f18px tw-flex tw-flex-col tw-gap-[16px] md:tw-gap-[32px]">
            {allReviews?.length ? 'Ratings & Reviews' : 'No Reviews yet'}
            {productReviewCount?.ratingCount ? (
              <div>
                <div className="flex items-center">
                  <p className="mv0 fw6 c44454A pr8px tw-text-[30px]">
                    {productReviewCount?.avgRating?.toFixed(1)}
                  </p>
                  <div className="flex items-end">
                    <RatingStar rating={productReviewCount?.avgRating} />
                    <p className="mv0 pl16px fw5 f14px c44454A">
                      <span>{productReviewCount?.ratingCount}</span> Ratings &{' '}
                      {productReviewCount?.reviewCount} Reviews
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </h2>
          {userData?.isLogin ? (
            <>
              <button
                style={{
                  opacity: shouldReviewItem() ? 1 : 0.21,
                  border: `1px solid ${themeColor || '#5c3cb7'}`,
                  color: themeColor || '#5b3bb7',
                }}
                className={classNames(
                  styles.writeReviewBtn,
                  '!tw-w-full md:!tw-w-[130px]'
                )}
                onClick={handleCustomerReviewModalOpen}
                data-tip
                data-for="write-a-review"
              >
                {loggedInUserReview?.review ? 'Edit Review' : 'Write a Review'}
              </button>
              <ReactTooltip
                id="write-a-review"
                place="bottom"
                effect="solid"
                multiline={true}
                padding="8px"
                disable={shouldReviewItem() || deviceWidth <= 767}
              >
                <p className="mv0 fw5 f12px">
                  Only buyers of this item can submit reviews. If you have any
                  <br />
                  concerns, please share them with us and we would look into it.
                </p>
              </ReactTooltip>
            </>
          ) : null}
        </div>
        <ProductReviewMedia
          reviews={reviewsWithMedia}
          handleProductReviewModalOpen={handleProductReviewModalOpen}
          canLoadMore={reviewWithMediaPaginationConfig.isNextPage}
          onLoadMore={() => getProductReviewsWithMedia()}
        />
        {!!allReviews?.length && (
          <>
            <div className="tw-mt-[20px] tw-border-t tw-border-solid tw-border-[#DEDEDE] md:tw-mt-[36px]">
              <h2 className="tw-m-0 tw-pt-[32px] tw-text-[18px]/[20px] tw-font-medium tw-text-black md:tw-pt-[36px]">
                All Reviews
              </h2>
              {allReviews?.map((review, index) => (
                <CustomerReviewReply
                  review={review}
                  key={index}
                  handleProductReviewModalOpen={(mediaIndex) =>
                    handleProductReviewModalOpen(index, mediaIndex, REVIEW_TYPE.ALL)
                  }
                />
              ))}
            </div>
            {!!allReviewPaginationConfig?.isNextPage && (
              <button
                onClick={() => getAllProductReviews()}
                style={{
                  color: themeColor,
                  borderColor: themeColor,
                }}
                className="tw-mx-auto tw-mt-[18px] tw-h-[54px] tw-w-[242px] tw-border tw-border-solid tw-bg-transparent"
              >
                See more reviews
              </button>
            )}
          </>
        )}
      </div>
      <CustomerReviewModal
        open={openCustomerReviewModal}
        onClose={() => setOpenCustomerReviewModal(false)}
        onSubmit={handleCustomerReviewModalClose}
        product={product}
        loggedInUserReview={loggedInUserReview}
      />
      <VerifiedBuyerModal
        open={openVerifiedModal}
        onClose={() => setOpenVerifiedModal(false)}
      />
      {productReviewModalState.show && (
        <ProductReviewModal
          modalState={productReviewModalState}
          reviews={productReviewModalReviews}
          setModalState={setProductReviewModalState}
        />
      )}
    </>
  );
};

export default ReviewsSection;
